import {
  Component,
  computed,
  effect,
  inject,
  input,
  linkedSignal,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { NgClass } from '@angular/common';
import { Store } from '@ngrx/store';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { RFIAnswerOption, RFIAnswerStatus, RFICompanyGroup } from '@app/store/rfis/rfi.types';
import { StyledOptionsListComponent } from '@app/framework/overlays/styled-options-list/styled-options-list.component';
import { UserDriveFile } from '@app/framework/constants/documents.constants';
import { OPTIONS } from '@app/framework/constants/options-list.constants';
import {
  RFI_DETAILS_TABS,
  RFI_OPTIONS_MANAGER_AND_RESPONSIBLE_ANSWERED,
  RFI_OPTIONS_MANAGER_ANSWERED_BY_OTHER_USER,
  RFI_OPTIONS_MANAGER_CLOSED,
  RFI_OPTIONS_MANAGER_HAS_ANSWER_APPROVED,
  RFI_OPTIONS_MANAGER_HAS_ANSWER_APPROVED_ANSWERED_BY_OTHER_USER,
  RFI_OPTIONS_MANAGER_HAS_ANSWER_NON_APPROVED,
  RFI_OPTIONS_MANAGER_NO_ANSWER,
  RFI_OPTIONS_MANAGER_NO_ANSWER_DRAFT,
  RFI_OPTIONS_MANAGER_VOID,
  RFI_OPTIONS_PARTICIPANT,
  RFI_OPTIONS_RESPONSIBLE_HAS_ANSWER_APPROVED,
  RFI_OPTIONS_RESPONSIBLE_HAS_ANSWER_NON_APPROVED,
  RFI_OPTIONS_RESPONSIBLE_NO_ANSWER,
  RFI_OPTIONS_RESPONSIBLE_VOID,
} from '@app/store/rfis/rfi.constants';
import { UnderlineTabsComponent } from '@app/framework/underline-tabs/underline-tabs.component';
import { Tab, TAB_OPTIONS } from '@app/framework/underline-tabs/underline-tabs.types';
import { StatusBlockComponent } from '@app/pages/webapp/projects/view-project/rfi-listing/status-block/status-block.component';
import { DayjsFormatterPipe } from '@app/pipes/framework/dayjs-formatter.pipe';
import { CurrentUserService } from '@app/services/current-user.service';
import { InteractionBarStateService } from '@app/services/interaction-bar-state.service';
import { INTERACTION_BAR_STATES } from '@app/framework/constants/interaction-bar.constants';
import { RFIActions } from '@app/store/rfis/rfis.actions';
import { RFISelectors } from '@app/store/rfis/rfis.selectors';
import { FileCardComponent } from '@app/framework/file-handling/file-card-component/file-card.component';
import { FloatingTextareaComponent } from '@app/framework/inputs/floating-textarea/floating-textarea.component';
import { SimpleButtonComponent } from '@app/framework/buttons/simple-medium-button/simple-button.component';
import { PresignedFileUploadService } from '@app/services/presigned-file-upload.service';
import { MoneyPipe } from '@app/pipes/framework/money-short.pipe';
import { NotificationV2Service } from '@app/notification-v2.service';
import { DiscussionsViewComponent } from '@app/framework/messages/discussions-view/discussions-view.component';
import { createOrUpdateMessage, messagesActions } from '@app/store/messages/messages.actions';
import { MESSAGING_VIEWS } from '@app/store/messages/messages.interfaces';
import { MessagingFooterService } from '@app/services/messaging-footer.service';
import { DiscussionThreadViewComponent } from '@app/framework/messages/discussion-thread-view/discussion-thread-view.component';
import { getMessagingView } from '@app/store/messages/messages.selectors';
import { ExpansionPanelV2Component } from '@app/framework/expansion-panel-v2/expansion-panel-v2.component';
import { UserTypePipe } from '@app/pipes/framework/user-type.pipe';
import {
  ManagedFile,
  UploadMetaData,
  isPersistedFile,
  isPendingFile,
} from '@app/framework/interfaces/file.type';
import { InputUploadComponent } from '@app/framework/file-handling/input-upload/input-upload.component';

@Component({
  selector: 'app-rfi-details-sidebar',
  imports: [
    StyledOptionsListComponent,
    UnderlineTabsComponent,
    StatusBlockComponent,
    DayjsFormatterPipe,
    NgClass,
    FileCardComponent,
    FloatingTextareaComponent,
    FormsModule,
    SimpleButtonComponent,
    InputUploadComponent,
    MoneyPipe,
    DiscussionsViewComponent,
    DiscussionThreadViewComponent,
    ExpansionPanelV2Component,
    UserTypePipe,
  ],
  templateUrl: './rfi-details-sidebar.component.html',
  styleUrl: './rfi-details-sidebar.component.scss',
})
export class RfiDetailsSidebarComponent implements OnInit, OnDestroy {
  protected readonly userService = inject(CurrentUserService);
  protected readonly interactionBarStateService = inject(InteractionBarStateService);
  protected readonly store = inject(Store);
  protected readonly notif = inject(NotificationV2Service);
  protected readonly messagesFooterService = inject(MessagingFooterService);
  protected readonly presignedFileUploadService = inject(PresignedFileUploadService);

  rfiId = input<number>();
  rfi = toSignal(this.store.select(RFISelectors.selectRFIDetails));
  selectedMessagingView = toSignal(this.store.select(getMessagingView));
  rfiCompanies = toSignal(this.store.select(RFISelectors.selectRFICompanies));
  MESSAGING_VIEWS = signal(MESSAGING_VIEWS).asReadonly();

  tabs = signal<Tab[]>(RFI_DETAILS_TABS);
  tabOptions = signal(TAB_OPTIONS).asReadonly();
  selectedTab = signal<TAB_OPTIONS>(TAB_OPTIONS.RFI_DETAILS);
  isAnswerEditing = signal<boolean>(false);
  isQuestionEditing = signal<boolean>(false);
  isExpanded = signal<Record<string, boolean>>({});

  // I know, I know. I'm not that proud of this code either. But deadlines...
  menuOptions = computed(() => {
    const hasAnswer = !!this.rfi()?.responsible_answer || !!this.rfi()?.manager_answer;
    const isRFIManager = this.rfi()?.rfi_manager?.id === this.userService.data?.id;
    const isRFIResponsible = this.rfi()?.rfi_responsible?.id === this.userService.data?.id;
    // it's only answered by the other user, not by the current user.
    const answeredByOtherUser =
      (isRFIManager && !this.rfi()?.manager_answer && !!this.rfi().responsible_answer) ||
      (isRFIResponsible && !this.rfi()?.responsible_answer && !!this.rfi().manager_answer);
    const answerApproved = this.rfi()?.manager_answer_status === 'approved';
    const closedRFI = this.rfi()?.status === 'closed';
    const draftRFI = this.rfi()?.status === 'draft';
    const voidedRFI = this.rfi()?.status === 'void';

    if (draftRFI) {
      return RFI_OPTIONS_MANAGER_NO_ANSWER_DRAFT;
    }

    if (isRFIManager && voidedRFI) {
      return RFI_OPTIONS_MANAGER_VOID;
    }
    if (isRFIResponsible && voidedRFI) {
      return RFI_OPTIONS_RESPONSIBLE_VOID;
    }

    if (isRFIManager && closedRFI) {
      return RFI_OPTIONS_MANAGER_CLOSED;
    }

    if (isRFIManager && answeredByOtherUser && answerApproved) {
      return RFI_OPTIONS_MANAGER_HAS_ANSWER_APPROVED_ANSWERED_BY_OTHER_USER;
    }

    if (isRFIManager && hasAnswer && answerApproved) {
      return RFI_OPTIONS_MANAGER_HAS_ANSWER_APPROVED;
    }

    if (isRFIManager && !hasAnswer) {
      return RFI_OPTIONS_MANAGER_NO_ANSWER;
    }

    if (isRFIManager && isRFIResponsible && hasAnswer) {
      return RFI_OPTIONS_MANAGER_AND_RESPONSIBLE_ANSWERED;
    }

    if (isRFIManager && answeredByOtherUser) {
      return RFI_OPTIONS_MANAGER_ANSWERED_BY_OTHER_USER;
    }

    if (isRFIManager && hasAnswer && !answerApproved) {
      return RFI_OPTIONS_MANAGER_HAS_ANSWER_NON_APPROVED;
    }

    if (isRFIResponsible && (!hasAnswer || answeredByOtherUser)) {
      return RFI_OPTIONS_RESPONSIBLE_NO_ANSWER;
    }

    if (isRFIResponsible && hasAnswer && !answerApproved) {
      return RFI_OPTIONS_RESPONSIBLE_HAS_ANSWER_NON_APPROVED;
    }
    if (isRFIResponsible && hasAnswer && answerApproved) {
      return RFI_OPTIONS_RESPONSIBLE_HAS_ANSWER_APPROVED;
    }

    return RFI_OPTIONS_PARTICIPANT;
  });

  disabledOptions = computed<OPTIONS[]>(() => {
    return this.menuOptions()
      .filter((option) => option.customClass.includes('disabled'))
      .map((option) => option.value);
  });

  answerOptions = computed<RFIAnswerOption[]>(() => {
    const userId = this.userService.data?.id;

    if (!this.rfi()) {
      return [];
    }

    if (this.rfi().status === 'void' && this.rfi().rfi_manager?.id === userId) {
      return [
        {
          value: OPTIONS.RE_OPEN,
          label: 'Re-open',
          icon: 'icon-reopen',
          class: '',
        },
      ];
    }

    if (this.rfi().status === 'void') {
      return [];
    }

    if (this.rfi().rfi_manager?.id === userId) {
      const disabled = this.rfi()?.status === 'draft';
      return [
        {
          value: OPTIONS.ADD_ANSWER,
          label: 'Add Answer',
          disabled,
          icon: 'icon-answer-bubble',
          class: '',
        },
        {
          value: OPTIONS.VOID,
          label: 'Void',
          disabled,
          icon: 'icon-void',
          class: disabled ? '' : 'text-red-600',
        },
      ];
    }

    if (this.rfi().rfi_responsible?.id === userId) {
      return [
        { value: OPTIONS.ADD_ANSWER, label: 'Answer', icon: 'icon-answer-bubble', class: '' },
      ];
    }

    if (
      this.rfi()
        .participants.map((p) => p?.id)
        .includes(userId)
    ) {
      return [
        {
          value: OPTIONS.VIEW_DISCUSSIONS,
          label: 'View Discussions',
          icon: 'icon-discussions-bubble',
          class: '',
        },
      ];
    }

    return [];
  });

  rfiNumberOnly = computed(() => {
    if (!this.rfi()) {
      return '';
    }
    return this.rfi().rfi_number.replace('RFI.', '');
  });

  readonly questionText = linkedSignal(() => {
    return this.rfi()?.question ?? '';
  });

  readonly answerText = linkedSignal(() => {
    const userId = this.userService.data.id;

    if (this.rfi()?.rfi_responsible?.id === userId) {
      return this.rfi()?.responsible_answer ?? '';
    }
    return this.rfi()?.manager_answer ?? '';
  });

  readonly userAnswerStatus = computed(() => {
    const userId = this.userService.data.id;
    if (this.rfi()?.rfi_manager?.id === userId) {
      return this.rfi()?.manager_answer_status ?? null;
    }
    return this.rfi()?.responsible_answer_status ?? null;
  });

  readonly answerFiles = linkedSignal<ManagedFile[]>(() => this.rfi()?.answer_files ?? []);
  readonly questionFiles = linkedSignal<ManagedFile[]>(() => this.rfi()?.question_files ?? []);
  readonly newAnswerFiles = computed(() =>
    this.answerFiles().filter((file) => isPendingFile(file)),
  );
  readonly newQuestionFiles = computed(() =>
    this.questionFiles().filter((file) => isPendingFile(file)),
  );
  readonly answerFilesUploading = signal<boolean>(false);
  readonly questionFilesUploading = signal<boolean>(false);

  readonly answerUploadMetadata = computed<Partial<UploadMetaData>[] | null>(() => {
    return this.newAnswerFiles().map(() => ({
      rfi: {
        id: this.rfi()?.id,
        belongs_to: 'answer',
      },
    }));
  });
  readonly questionUploadMetadata = computed<Partial<UploadMetaData>[] | null>(() => {
    return this.newQuestionFiles().map(() => ({
      rfi: {
        id: this.rfi()?.id,
        belongs_to: 'question',
      },
    }));
  });

  readonly answerFilesToDelete = new Set<number>();
  readonly questionFilesToDelete = new Set<number>();
  answerStatus: RFIAnswerStatus;

  // I know, I know... It's ugly as hell. But it's working at least.
  // We're in a hurry, so... sorry future me.
  effectRef = effect(() => {
    const groupId = this.rfi()?.messaging_group?.id;
    if (!groupId) {
      console.warn('RFI Details Sidebar: No messaging group associated with this RFI');
      return;
    }
    this.store.dispatch(messagesActions.setGroupFilters({ filters: { group_id: groupId } }));
    this.store.dispatch(messagesActions.loadFirstPageOfGroups({ clearOldGroups: false }));
    this.store.dispatch(
      messagesActions.setMessagingView({ view: MESSAGING_VIEWS.DISCUSSION_VIEW }),
    );
    this.store.dispatch(messagesActions.setSelectedGroupId({ groupId }));
    this.store.dispatch(messagesActions.loadMessages());
  });

  isDestroyed$ = new Subject<void>();

  ngOnInit() {
    this.store.dispatch(RFIActions.onOpenRFIDetails({ rfiId: this.rfiId() }));

    this.messagesFooterService.save.pipe(takeUntil(this.isDestroyed$)).subscribe(() => {
      this.store.dispatch(createOrUpdateMessage({}));
    });
  }

  ngOnDestroy() {
    this.isDestroyed$.next();
    this.isDestroyed$.complete();
  }

  handleOptionSelected(option: OPTIONS) {
    switch (option) {
      case OPTIONS.EDIT_ANSWER:
      case OPTIONS.ADD_ANSWER: {
        this.isAnswerEditing.set(true);
        break;
      }
      case OPTIONS.EDIT_QUESTION: {
        this.isQuestionEditing.set(true);
        break;
      }
      case OPTIONS.EDIT_RFI: {
        this.interactionBarStateService.openInteractionBar(INTERACTION_BAR_STATES.CREATE_RFI, {
          rfi: this.rfi(),
        });
        break;
      }
      case OPTIONS.APPROVE_ANSWER: {
        this.notif
          .showConfirmDialog('Are you sure you want to approve this answer?')
          .pipe(takeUntil(this.isDestroyed$))
          .subscribe((resp) => {
            if (resp) {
              this.store.dispatch(RFIActions.approveAnswer({ rfiId: this.rfiId() }));
            }
          });
        break;
      }
      case OPTIONS.DOWNLOAD_COVER: {
        const hash = this.notif.showLoading('Coming soon...');
        setTimeout(() => {
          this.notif.close(hash);
        }, 2500);
        break;
      }
      case OPTIONS.MARK_OPEN: {
        this.store.dispatch(RFIActions.markRFIOpen({ rfiId: this.rfiId() }));
        break;
      }
      case OPTIONS.CLOSE_RFI: {
        this.store.dispatch(RFIActions.closeRFI({ rfiId: this.rfiId() }));
        break;
      }
      case OPTIONS.RE_OPEN: {
        this.notif
          .showConfirmDialog('Are you sure you want to re-open this RFI?')
          .pipe(takeUntil(this.isDestroyed$))
          .subscribe((resp) => {
            if (resp) {
              this.store.dispatch(RFIActions.reOpenRFI({ rfiId: this.rfiId() }));
            }
          });
        break;
      }
      case OPTIONS.VOID: {
        this.notif
          .showConfirmDialog('Are you sure you want to void this RFI?')
          .pipe(takeUntil(this.isDestroyed$))
          .subscribe((resp) => {
            if (resp) {
              this.store.dispatch(RFIActions.voidRFI({ rfiId: this.rfiId() }));
            }
          });

        break;
      }
      case OPTIONS.DELETE: {
        this.notif
          .showConfirmDialog('Are you sure you want to delete this RFI?')
          .pipe(takeUntil(this.isDestroyed$))
          .subscribe((resp) => {
            if (resp) {
              this.store.dispatch(RFIActions.deleteRFI({ rfiId: this.rfiId() }));
            }
          });
        break;
      }
      case OPTIONS.VIEW_DISCUSSIONS: {
        this.selectedTab.set(TAB_OPTIONS.DISCUSSIONS);
      }
    }
  }

  protected onAnswerFilesChange(files: (File | ManagedFile | UserDriveFile)[]) {
    this.answerFiles.set(files as ManagedFile[]);
  }
  protected onQuestionFilesChange(files: (File | ManagedFile | UserDriveFile)[]) {
    this.questionFiles.set(files as ManagedFile[]);
  }

  protected removeAnswerFile(event: { file: ManagedFile; index: number }) {
    this.answerFiles.update((files) => files.filter((_, i) => i !== event?.index));

    if (isPersistedFile(event?.file)) {
      this.answerFilesToDelete.add(event.file.id);
    }
  }

  protected removeQuestionFile(event: { file: ManagedFile; index: number }) {
    this.questionFiles.update((files) => files.filter((_, i) => i !== event?.index));
    if (isPersistedFile(event?.file)) {
      this.questionFilesToDelete.add(event.file.id);
    }
  }

  initiateAnswerSave(status: 'submitted' | 'draft') {
    this.answerStatus = status;

    if (!this.newAnswerFiles()?.length) {
      this.saveAnswer();
    } else {
      this.uploadAndSaveAnswer();
    }
  }
  initiateQuestionSave() {
    if (!this.questionText()?.trim()) {
      this.notif.showError('Question text cannot be empty.');
      return;
    }

    if (!this.newQuestionFiles()?.length) {
      this.saveQuestion();
    } else {
      this.uploadAndSaveQuestion();
    }
  }

  saveAnswer() {
    this.store.dispatch(
      RFIActions.saveAnswer({
        answerText: this.answerText(),
        status: this.answerStatus,
        rfiId: this.rfiId(),
        filesToDelete: [...this.answerFilesToDelete.values()],
      }),
    );
    this.isAnswerEditing.set(false);
    this.answerFilesToDelete.clear();
  }

  saveQuestion() {
    this.store.dispatch(
      RFIActions.saveQuestion({
        questionText: this.questionText(),
        rfiId: this.rfiId(),
        filesToDelete: [...this.questionFilesToDelete.values()],
      }),
    );

    this.isQuestionEditing.set(false);
    this.questionFilesToDelete.clear();
  }

  uploadAndSaveAnswer() {
    this.answerFilesUploading.set(true);
    this.presignedFileUploadService
      .uploadMultipleFilesToS3(this.newAnswerFiles(), this.answerUploadMetadata())
      .subscribe(() => {
        this.answerFilesUploading.set(false);
        this.saveAnswer();
      });
  }

  uploadAndSaveQuestion() {
    this.questionFilesUploading.set(true);
    this.presignedFileUploadService
      .uploadMultipleFilesToS3(this.newQuestionFiles(), this.questionUploadMetadata())
      .subscribe(() => {
        this.questionFilesUploading.set(false);
        this.saveQuestion();
      });
  }

  toggleCompanyExpand(company: RFICompanyGroup) {
    const currentValue = this.isExpanded()?.[company.company_name] || false;
    this.isExpanded.update((expandedMap) => {
      return {
        ...expandedMap,
        [company.company_name]: !currentValue,
      };
    });
  }
}
